class ProductEmbroidery extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('[data-embroidery-modal]');
    this.digitisationInfoModal = this.querySelector('[data-digitisation-info-modal]');
    this.openButton = this.querySelector('[data-embroidery-open]');
    this.openButtonLabel = this.querySelector('[data-embroidery-open-label]');
    this.summary = this.querySelector('[data-embroidery-summary]');
    this.error = this.querySelector('[data-embroidery-error]');
    this.choicePanel = this.querySelector('[data-embroidery-panel="choice"]');
    this.formPanel = this.querySelector('[data-embroidery-panel="form"]');
    this.formTitle = this.querySelector('[data-embroidery-form-title]');
    this.formIntro = this.querySelector('[data-embroidery-form-intro]');
    this.textFields = this.querySelector('[data-embroidery-text-fields]');
    this.logoFields = this.querySelector('[data-embroidery-logo-fields]');
    this.logoNote = this.querySelector('[data-embroidery-logo-note]');
    this.addToCartButton = this.querySelector('[data-embroidery-add-to-cart]');
    this.addToCartSpinner = this.querySelector('[data-embroidery-add-spinner]');
    this.addToCartButtonLabel = this.addToCartButton?.querySelector('span');
    this.addonTotal = this.querySelector('[data-embroidery-addon-total]');
    this.colourRow = this.querySelector('[data-embroidery-colour-row]');
    this.colourValue = this.querySelector('[data-embroidery-colour-value]');
    this.colourSwatch = this.querySelector('[data-embroidery-colour-swatch]');
    this.sizeInputs = Array.from(this.querySelectorAll('[data-embroidery-size-value]'));
    this.textPositionFields = this.querySelector('[data-embroidery-text-position-fields]');
    this.logoPositionFields = this.querySelector('[data-embroidery-logo-position-fields]');
    this.line1 = this.querySelector('[data-embroidery-line-1]');
    this.line2 = this.querySelector('[data-embroidery-line-2]');
    this.textPositionInputs = Array.from(this.querySelectorAll('[data-text-position]'));
    this.logoPositionInputs = Array.from(this.querySelectorAll('[data-logo-position]'));
    this.logoName = this.querySelector('[data-embroidery-logo-name]');
    this.digitisationInputs = Array.from(this.querySelectorAll('[data-embroidery-digitisation-choice]'));
    this.logoUploadFields = this.querySelector('[data-logo-upload-fields]');
    this.uploadTarget = this.querySelector('[data-upload-lift-target]');
    this.previewImage = this.querySelector('[data-embroidery-image]');
    this.embroideryProperty = this.querySelector('[data-embroidery-property]');
    this.textProperty = this.querySelector('[data-text-embroidery-property]');
    this.logoProperty = this.querySelector('[data-logo-embroidery-property]');
    this.digitisationProperty = this.querySelector('[data-embroidery-digitisation-property]');
    this.groupProperty = this.querySelector('[data-embroidery-group-property]');
    this.unitsProperty = this.querySelector('[data-embroidery-units-property]');
    this.digitizationRequiredProperty = this.querySelector('[data-digitization-required-property]');
    this.textSaved = false;
    this.logoSaved = false;
    this.activeEditor = null;
    this.scrollLock = null;
    this.productFormObserver = null;
    this.productFormSyncFrame = null;
    this.keyboardScrollTimeout = null;
    this.focusedInputScrollCleanup = null;

    this.openButton?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-embroidery-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.querySelectorAll('[data-embroidery-choice]').forEach((button) => {
      button.addEventListener('click', () => this.showForm(button.dataset.embroideryChoice));
    });
    this.querySelectorAll('[data-embroidery-remove-type]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.removeType(button.dataset.embroideryRemoveType);
      });
    });
    this.querySelector('[data-embroidery-back]')?.addEventListener('click', () => this.showChoice());
    this.querySelector('[data-embroidery-save]')?.addEventListener('click', () => this.save());
    this.addToCartButton?.addEventListener('click', () => this.submitProductForm());
    this.sizeInputs.forEach((input) => {
      input.addEventListener('change', () => this.syncProductSizeFromModal(input.value));
    });
    this.querySelector('[data-digitisation-info-open]')?.addEventListener('click', () => this.openDigitisationInfo());
    this.querySelectorAll('[data-digitisation-info-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDigitisationInfo());
    });
    this.digitisationInfoModal?.querySelector('.embroidery-info-modal__dialog')?.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    this.digitisationInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateLogoUploadVisibility();
        if (!this.logoNeedsDigitisation()) this.clearUploadLiftField();
      });
    });
    [this.line1, this.line2].forEach((input) => {
      input?.addEventListener('focus', () => this.lockFocusedInputInModalView(input));
      input?.addEventListener('blur', () => this.releaseFocusedInputScrollLock());
    });

    this.appendModalToBody();
    this.bridgeUploadLiftField();
    this.updateLogoUploadVisibility();
    this.updateStateProperties();
    this.syncModalVariantState();
    this.observeProductFormState();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.updatePreviewImage(event.data?.variant);
        this.syncModalVariantState();
      });
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    if (this.productFormObserver) this.productFormObserver.disconnect();
    if (this.productFormSyncFrame) window.cancelAnimationFrame(this.productFormSyncFrame);
    if (this.keyboardScrollTimeout) window.clearTimeout(this.keyboardScrollTimeout);
    this.releaseFocusedInputScrollLock();
    if (this.modal && this.modal.parentElement === document.body) this.appendChild(this.modal);
  }

  appendModalToBody() {
    if (this.modal) document.body.appendChild(this.modal);
  }

  open() {
    this.appendModalToBody();
    this.showChoice();
    this.syncModalVariantState();
    this.modal.hidden = false;
    this.lockBackgroundScroll();
  }

  close() {
    this.closeDigitisationInfo();
    this.modal.hidden = true;
    this.unlockBackgroundScroll();
    this.clearError();
    if (this.keyboardScrollTimeout) {
      window.clearTimeout(this.keyboardScrollTimeout);
      this.keyboardScrollTimeout = null;
    }
    this.releaseFocusedInputScrollLock();
  }

  lockFocusedInputInModalView(input) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const scroller = this.modal?.querySelector('.embroidery-modal__content');
    if (!scroller || !input) return;
    this.releaseFocusedInputScrollLock();

    let animationFrame = null;
    const scrollIntoView = () => {
      const inputRect = input.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const visualViewportBottom = window.visualViewport
        ? window.visualViewport.offsetTop + window.visualViewport.height
        : window.innerHeight;
      const visibleBottom = Math.min(scrollerRect.bottom, visualViewportBottom) - 24;
      const visibleTop = scrollerRect.top + 16;

      if (inputRect.bottom > visibleBottom) {
        scroller.scrollTop += inputRect.bottom - visibleBottom;
      } else if (inputRect.top < visibleTop) {
        scroller.scrollTop -= visibleTop - inputRect.top;
      }
    };
    const queueScrollIntoView = () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(scrollIntoView);
    };

    scrollIntoView();
    if (this.keyboardScrollTimeout) window.clearTimeout(this.keyboardScrollTimeout);
    this.keyboardScrollTimeout = window.setTimeout(scrollIntoView, 320);
    scroller.addEventListener('scroll', queueScrollIntoView, { passive: true });
    window.visualViewport?.addEventListener('resize', queueScrollIntoView);
    window.visualViewport?.addEventListener('scroll', queueScrollIntoView);

    this.focusedInputScrollCleanup = () => {
      scroller.removeEventListener('scroll', queueScrollIntoView);
      window.visualViewport?.removeEventListener('resize', queueScrollIntoView);
      window.visualViewport?.removeEventListener('scroll', queueScrollIntoView);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
    };
  }

  releaseFocusedInputScrollLock() {
    if (!this.focusedInputScrollCleanup) return;
    this.focusedInputScrollCleanup();
    this.focusedInputScrollCleanup = null;
  }

  lockBackgroundScroll() {
    if (this.scrollLock) return;

    const x = window.scrollX || window.pageXOffset || 0;
    const y = window.scrollY || window.pageYOffset || 0;
    const bodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    this.scrollLock = { x, y, bodyStyle };

    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = `-${x}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('overflow-hidden', 'embroidery-modal-open');
  }

  unlockBackgroundScroll() {
    const lock = this.scrollLock;

    document.body.classList.remove('overflow-hidden', 'embroidery-modal-open');
    if (!lock) return;

    document.body.style.position = lock.bodyStyle.position;
    document.body.style.top = lock.bodyStyle.top;
    document.body.style.left = lock.bodyStyle.left;
    document.body.style.width = lock.bodyStyle.width;
    document.body.style.overflow = lock.bodyStyle.overflow;
    window.scrollTo(lock.x, lock.y);
    this.scrollLock = null;
  }

  openDigitisationInfo() {
    if (!this.digitisationInfoModal) return;
    this.digitisationInfoModal.hidden = false;
  }

  closeDigitisationInfo() {
    if (!this.digitisationInfoModal) return;
    this.digitisationInfoModal.hidden = true;
  }

  submitProductForm() {
    if (this.addToCartButton?.disabled || this.addToCartButton?.getAttribute('aria-disabled') === 'true') return;
    const submitButton = this.getActiveProductSubmitButton();
    submitButton?.click();
  }

  setAddToCartLoading(isLoading) {
    if (!this.addToCartButton) return;

    this.addToCartButton.classList.toggle('loading', isLoading);
    this.addToCartButton.toggleAttribute('disabled', isLoading);
    if (isLoading) {
      this.addToCartButton.setAttribute('aria-disabled', 'true');
      this.addToCartSpinner?.classList.remove('hidden');
    } else {
      this.addToCartButton.removeAttribute('aria-disabled');
      this.addToCartSpinner?.classList.add('hidden');
      this.updateModalAddToCartState();
    }
  }

  syncProductSizeFromModal(value) {
    const sizeInput = this.findProductOptionInput('size', value);
    if (!sizeInput || sizeInput.checked || this.isProductOptionUnavailable(sizeInput)) return;

    sizeInput.checked = true;
    sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncModalVariantState();
  }

  syncModalVariantState() {
    this.updateModalSizeState();
    this.updateModalColourState();
    this.updateModalAddToCartState();
  }

  updateModalSizeState() {
    if (!this.sizeInputs.length) return;

    const selectedSize = this.findSelectedProductOptionValue('size');
    this.sizeInputs.forEach((input) => {
      input.checked = input.value === selectedSize;
      const productInput = this.findProductOptionInput('size', input.value);
      const unavailable = this.isProductOptionUnavailable(productInput);
      input.disabled = unavailable;
      input.classList.toggle('disabled', unavailable);
      input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    });
  }

  updateModalColourState() {
    const activeSwatch = this.findActiveColourSwatch();
    const selectedColour = this.findSelectedProductOptionValue('colour')
      || this.findSelectedProductOptionValue('color')
      || this.getSwatchColourName(activeSwatch);

    if (this.colourValue && selectedColour) this.colourValue.textContent = selectedColour;
    if (this.colourRow) this.colourRow.hidden = !selectedColour && !activeSwatch;
    if (!this.colourSwatch) return;

    this.colourSwatch.innerHTML = '';
    if (activeSwatch) {
      const clone = activeSwatch.cloneNode(true);
      clone.removeAttribute('role');
      clone.removeAttribute('tabindex');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('.webyzeTooltip').forEach((element) => element.remove());
      this.colourSwatch.appendChild(clone);
      return;
    }

    const colourInput = this.findProductOptionInput('colour', selectedColour) || this.findProductOptionInput('color', selectedColour);
    const colourLabel = colourInput?.id
      ? this.getProductOptionRoot()?.querySelector(`label[for="${this.escapeSelector(colourInput.id)}"]`)
      : null;
    if (!colourLabel) return;

    const clone = colourLabel.cloneNode(true);
    clone.removeAttribute('for');
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('input, .visually-hidden').forEach((element) => element.remove());
    this.colourSwatch.appendChild(clone);
  }

  updateModalAddToCartState() {
    if (!this.addToCartButton || this.addToCartButton.classList.contains('loading')) return;

    const submitButton = this.getActiveProductSubmitButton();
    const submitText = submitButton?.querySelector('span')?.textContent?.trim() || submitButton?.textContent?.trim();
    const disabled = !submitButton || Boolean(submitButton.disabled || submitButton.hasAttribute('disabled') || submitButton.getAttribute('aria-disabled') === 'true');

    this.addToCartButton.toggleAttribute('disabled', disabled);
    if (disabled) {
      this.addToCartButton.setAttribute('aria-disabled', 'true');
    } else {
      this.addToCartButton.removeAttribute('aria-disabled');
    }
    if (this.addToCartButtonLabel && submitText) this.addToCartButtonLabel.textContent = submitText;
    this.syncModalAddToCartButtonStyle(submitButton);
  }

  getActiveProductSubmitButton() {
    const productForm = this.closest('product-form');
    const form = this.closest('form') || productForm?.querySelector('form[action*="/cart/add"]') || productForm?.querySelector('form');
    const scope = form || productForm;
    if (!scope) return null;

    const submitButtons = Array.from(scope.querySelectorAll('button[name="add"], [type="submit"], .gPreorderBtn'))
      .filter((button) => button !== this.addToCartButton && button.matches('button, input'));
    const visiblePreorderButton = submitButtons.find((button) => button.classList.contains('gPreorderBtn') && this.isElementVisible(button));
    if (visiblePreorderButton) return visiblePreorderButton;

    const visibleSubmitButton = submitButtons.find((button) => !button.classList.contains('gPreorderHidden') && this.isElementVisible(button));
    if (visibleSubmitButton) return visibleSubmitButton;

    return submitButtons.find((button) => !button.classList.contains('gPreorderHidden')) || submitButtons[0] || null;
  }

  isElementVisible(element) {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  syncModalAddToCartButtonStyle(submitButton) {
    if (!this.addToCartButton || !submitButton) return;

    const styles = window.getComputedStyle(submitButton);
    this.addToCartButton.style.backgroundColor = styles.backgroundColor;
    this.addToCartButton.style.borderColor = styles.borderColor;
    this.addToCartButton.style.color = styles.color;
  }

  observeProductFormState() {
    const productForm = this.closest('product-form');
    const form = this.closest('form') || productForm?.querySelector('form[action*="/cart/add"]') || productForm?.querySelector('form');
    const target = form || productForm;
    if (!target || typeof MutationObserver === 'undefined') return;

    const queueSync = () => {
      if (this.productFormSyncFrame) window.cancelAnimationFrame(this.productFormSyncFrame);
      this.productFormSyncFrame = window.requestAnimationFrame(() => {
        this.productFormSyncFrame = null;
        this.syncModalVariantState();
      });
    };

    this.productFormObserver = new MutationObserver(queueSync);
    this.productFormObserver.observe(target, {
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled', 'aria-disabled', 'value'],
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  findActiveColourSwatch(selectedColour) {
    const productInfo = this.closest('product-info') || document;
    const activeSwatch = productInfo.querySelector('.swatchProductColor.currentSwatch');
    if (activeSwatch) return activeSwatch;

    if (!selectedColour) return null;

    return Array.from(productInfo.querySelectorAll('.swatchProductColor')).find((swatch) => {
      const label = `${swatch.dataset.name || ''} ${swatch.getAttribute('aria-label') || ''}`;
      return label.toLowerCase().includes(selectedColour.toLowerCase());
    }) || null;
  }

  getSwatchColourName(swatch) {
    if (!swatch) return '';
    const rawName = swatch.dataset.name || swatch.getAttribute('aria-label') || swatch.textContent || '';
    return rawName
      .replace(/^Color:\s*/i, '')
      .split(' - ')
      .pop()
      .trim();
  }

  isProductOptionUnavailable(input) {
    if (!input) return true;
    return Boolean(input.disabled || input.classList.contains('disabled') || input.getAttribute('aria-disabled') === 'true');
  }

  findSelectedProductOptionValue(optionName) {
    const fieldset = this.findProductOptionFieldset(optionName);
    return fieldset?.querySelector('input[type="radio"]:checked')?.value || '';
  }

  findProductOptionInput(optionName, value) {
    if (!value) return null;

    const fieldset = this.findProductOptionFieldset(optionName);
    return Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).find((input) => input.value === value) || null;
  }

  findProductOptionFieldset(optionName) {
    const normalizedName = optionName.toLowerCase();
    const root = this.getProductOptionRoot();

    return Array.from(root?.querySelectorAll('fieldset') || []).find((fieldset) => {
      const label = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
      if (normalizedName === 'colour') return label === 'colour' || label === 'color';
      return label === normalizedName;
    }) || null;
  }

  getProductOptionRoot() {
    const form = this.closest('form');
    return form?.querySelector('variant-radios, variant-selects') || this.closest('product-info')?.querySelector('variant-radios, variant-selects');
  }

  escapeSelector(value) {
    if (window.CSS?.escape) return CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  showChoice() {
    this.activeEditor = null;
    this.syncModalVariantState();
    this.updateStateProperties();
    this.choicePanel.classList.add('is-active');
    this.formPanel.classList.remove('is-active');
    this.clearError();
  }

  showForm(type) {
    this.activeEditor = type;
    const isText = type === 'text';
    const isLogo = type === 'logo';

    this.choicePanel.classList.remove('is-active');
    this.formPanel.classList.add('is-active');
    this.textFields.hidden = !isText;
    this.logoFields.hidden = !isLogo;
    this.logoNote.hidden = !isLogo;
    this.textPositionFields.hidden = !isText;
    this.logoPositionFields.hidden = !isLogo;
    this.line1.disabled = !isText;
    this.line2.disabled = !isText;
    this.logoName.disabled = !isLogo;
    this.textPositionInputs.forEach((input) => input.disabled = !isText);
    this.logoPositionInputs.forEach((input) => input.disabled = !isLogo);
    this.digitisationInputs.forEach((input) => input.disabled = !isLogo);
    this.updateLogoUploadVisibility();
    this.formTitle.textContent = isText ? 'Text Embroidery' : 'Logo Embroidery';
    if (this.formIntro) {
      this.formIntro.textContent = isText ? 'Add up to two lines of custom text.' : '';
    }
    this.clearError();
  }

  save() {
    if (!this.activeEditor) return;

    const line1 = this.line1.value.trim();

    if (this.activeEditor === 'text' && !line1) {
      this.showError('Line 1 is required for text embroidery.');
      return;
    }

    if (this.activeEditor === 'logo' && this.logoNeedsDigitisation() && !this.hasUploadLiftValue()) {
      this.showError('Please upload a logo file for digitisation before saving.');
      return;
    }

    if (this.activeEditor === 'logo' && !this.logoName.value.trim()) {
      this.showError('Logo Name is required for logo embroidery.');
      return;
    }

    if (this.activeEditor === 'text') this.textSaved = true;
    if (this.activeEditor === 'logo') this.logoSaved = true;

    this.showChoice();
  }

  clear() {
    this.resetState();
    this.showChoice();
  }

  resetAfterCartAdd() {
    this.resetState();
    this.showChoice();
    this.close();
  }

  resetState() {
    this.clearUploadLiftField();
    this.textSaved = false;
    this.logoSaved = false;
    this.activeEditor = null;
    this.embroideryProperty.value = 'No';
    [this.textProperty, this.logoProperty, this.groupProperty, this.unitsProperty, this.digitizationRequiredProperty, this.digitisationProperty].forEach((input) => {
      input.value = '';
      input.disabled = true;
    });
    this.line1.value = '';
    this.line2.value = '';
    this.line1.disabled = true;
    this.line2.disabled = true;
    this.logoName.value = '';
    this.logoName.disabled = true;
    this.textPositionInputs.forEach((input, index) => {
      input.checked = index === 0;
      input.disabled = true;
    });
    this.logoPositionInputs.forEach((input, index) => {
      input.checked = index === 1;
      input.disabled = true;
    });
    this.resetDigitisationChoice();
    this.summary.hidden = true;
    this.groupProperty.value = '';
    this.updateLogoUploadVisibility();
    this.updateStateProperties();
  }

  prepareGroup() {
    this.updateStateProperties();
    if (this.embroideryProperty.value !== 'Yes') return null;

    if (!this.groupProperty.value) {
      this.groupProperty.value = `embroidery-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    this.groupProperty.disabled = false;

    return {
      groupId: this.groupProperty.value,
      embroideryVariantId: parseInt(this.dataset.embroideryVariantId, 10),
      digitizationVariantId: parseInt(this.dataset.digitizationVariantId, 10),
      embroideryUnits: parseInt(this.unitsProperty.value || '0', 10),
      digitizationRequired: this.digitizationRequiredProperty.value === 'Yes'
    };
  }

  getAddonBreakdownProperties(quantity = 1) {
    const itemQuantity = Math.max(0, parseInt(quantity, 10) || 0);
    const embroideryUnitPrice = parseInt(this.dataset.embroideryUnitPriceCents || '0', 10);
    const embroidery = {};
    const digitization = {};

    if (this.textSaved && itemQuantity > 0) {
      embroidery['Text Embroidery'] = `x ${itemQuantity} = ${this.formatAddonBreakdownMoney(embroideryUnitPrice * itemQuantity)}`;
    }

    if (this.logoSaved && itemQuantity > 0) {
      embroidery['Logo Embroidery'] = `x ${itemQuantity} = ${this.formatAddonBreakdownMoney(embroideryUnitPrice * itemQuantity)}`;
    }

    if (this.logoSaved && this.logoNeedsDigitisation()) {
      digitization.Digitisation = 'x 1';
    }

    return { embroidery, digitization };
  }

  removeType(type) {
    if (type === 'text') {
      this.textSaved = false;
      this.line1.value = '';
      this.line2.value = '';
      this.textPositionInputs.forEach((input, index) => {
        input.checked = index === 0;
        input.disabled = true;
      });
    }

    if (type === 'logo') {
      this.clearUploadLiftField();
      this.logoSaved = false;
      this.logoName.value = '';
      this.logoName.disabled = true;
      this.resetDigitisationChoice();
      this.logoPositionInputs.forEach((input, index) => {
        input.checked = index === 1;
        input.disabled = true;
      });
      this.updateLogoUploadVisibility();
    }

    this.showChoice();
  }

  updateStateProperties() {
    const units = (this.textSaved ? 1 : 0) + (this.logoSaved ? 1 : 0);
    const hasEmbroidery = units > 0;
    const digitizationRequired = this.logoSaved && this.logoNeedsDigitisation();

    this.embroideryProperty.value = hasEmbroidery ? 'Yes' : 'No';
    this.textProperty.value = this.textSaved ? 'Yes' : '';
    this.textProperty.disabled = !this.textSaved;
    this.logoProperty.value = this.logoSaved ? 'Yes' : '';
    this.logoProperty.disabled = !this.logoSaved;
    this.unitsProperty.value = hasEmbroidery ? String(units) : '';
    this.unitsProperty.disabled = !hasEmbroidery;
    this.digitizationRequiredProperty.value = digitizationRequired ? 'Yes' : '';
    this.digitizationRequiredProperty.disabled = !digitizationRequired;
    this.digitisationProperty.value = this.logoSaved ? this.currentDigitisationLabel() : '';
    this.digitisationProperty.disabled = !this.logoSaved;
    this.groupProperty.disabled = !hasEmbroidery || !this.groupProperty.value;

    this.line1.disabled = !this.textSaved && this.activeEditor !== 'text';
    this.line2.disabled = !this.textSaved && this.activeEditor !== 'text';
    this.logoName.disabled = !this.logoSaved && this.activeEditor !== 'logo';
    this.textPositionInputs.forEach((input) => input.disabled = !this.textSaved && this.activeEditor !== 'text');
    this.logoPositionInputs.forEach((input) => input.disabled = !this.logoSaved && this.activeEditor !== 'logo');
    this.digitisationInputs.forEach((input) => input.disabled = !this.logoSaved && this.activeEditor !== 'logo');
    this.updateLogoUploadVisibility();

    this.updateChoiceState('text', this.textSaved);
    this.updateChoiceState('logo', this.logoSaved);
    this.updateAddonTotal(units);

    if (this.openButtonLabel) this.openButtonLabel.textContent = hasEmbroidery ? 'Edit Embroidery' : 'Add Embroidery';

    if (this.summary) {
      this.summary.textContent = '';
      this.summary.hidden = true;
    }
  }

  logoNeedsDigitisation() {
    return this.digitisationInputs.find((input) => input.checked)?.value === 'I need my logo digitised';
  }

  currentDigitisationLabel() {
    return this.digitisationInputs.find((input) => input.checked)?.value || 'I need my logo digitised';
  }

  resetDigitisationChoice() {
    this.digitisationInputs.forEach((input, index) => {
      input.checked = index === 0;
      input.disabled = true;
    });
  }

  updateLogoUploadVisibility() {
    const shouldShow = this.activeEditor === 'logo' && this.logoNeedsDigitisation();
    if (this.logoUploadFields) this.logoUploadFields.hidden = !shouldShow;
  }

  updateChoiceState(type, saved) {
    const root = this.modal || this;
    const row = root.querySelector(`[data-embroidery-choice-row="${type}"]`);
    const status = root.querySelector(`[data-embroidery-choice-status="${type}"]`);
    const hint = root.querySelector(`[data-embroidery-choice-hint="${type}"]`);
    const removeButton = root.querySelector(`[data-embroidery-remove-type="${type}"]`);
    const choice = root.querySelector(`[data-embroidery-choice="${type}"]`);

    row?.classList.toggle('is-saved', saved);
    if (status) status.hidden = !saved;
    if (hint) hint.hidden = saved;
    if (removeButton) removeButton.hidden = !saved;
    if (choice) choice.setAttribute('aria-label', `${saved ? 'Edit' : 'Add'} ${type} embroidery`);
  }

  updateAddonTotal(units) {
    if (!this.addonTotal) return;

    const unitPrice = parseInt(this.dataset.embroideryUnitPriceCents || '0', 10);
    const digitizationPrice = this.logoSaved && this.logoNeedsDigitisation()
      ? parseInt(this.dataset.digitizationPriceCents || '0', 10)
      : 0;

    this.addonTotal.textContent = this.formatMoney((unitPrice * units) + digitizationPrice);
  }

  formatMoney(cents) {
    const amount = (Math.max(0, cents) / 100).toFixed(2);
    const unitPrice = this.dataset.embroideryPrice || '';
    const match = unitPrice.match(/^(\D*)\s*[\d.,]+(\D*)$/);

    if (match) {
      const prefix = match[1].trim();
      const suffix = match[2].trim();
      if (prefix) return `${prefix} ${amount}`;
      if (suffix) return `${amount} ${suffix}`;
    }

    return amount;
  }

  formatAddonBreakdownMoney(cents) {
    const amount = (Math.max(0, cents) / 100).toFixed(2);

    return `R ${amount}`;
  }

  hasUploadLiftValue() {
    const logoInputs = this.getPropertyControls();
    const hasPropertyValue = logoInputs.some((input) => {
      const name = input.getAttribute('name') || '';
      const value = (input.value || '').trim();
      const text = input.closest('[class*="upload"], [id*="upload"], [data-upload-lift], [data-cloudlift]')?.textContent || '';
      if (input.type === 'file') return input.files?.length > 0;
      if (!/logo|upload|file/i.test(name + text)) return false;

      return /\/uploads\/|https?:\/\/|cdn\.shopify\.com|blob:|data:image\//i.test(value);
    });

    if (hasPropertyValue) return true;

    const uploadContext = this.uploadTarget?.textContent || '';
    const uploadLinks = Array.from(this.uploadTarget?.querySelectorAll('a[href], img[src], [data-url], [data-file-url], [data-upload-url]') || []);
    const hasUploadedAsset = uploadLinks.some((element) => {
      const value = element.getAttribute('href') || element.getAttribute('src') || element.dataset.url || element.dataset.fileUrl || element.dataset.uploadUrl || '';
      return /\/uploads\/|https?:\/\/|cdn\.shopify\.com|blob:|data:image\//i.test(value);
    });

    return /uploaded|upload complete|replace file|remove file/i.test(uploadContext) || hasUploadedAsset;
  }

  clearUploadLiftField() {
    if (!this.uploadTarget) return;

    const clearButtons = Array.from(this.uploadTarget.querySelectorAll('button, a, [role="button"]')).filter((element) => {
      const label = [
        element.textContent,
        element.getAttribute('aria-label'),
        element.getAttribute('title')
      ].filter(Boolean).join(' ');

      return /remove|delete|clear/i.test(label);
    });

    clearButtons.forEach((button) => button.click());

    this.uploadTarget.querySelectorAll('input').forEach((input) => {
      if (input.type === 'file' || /logo|upload|file/i.test(input.name || input.id || input.className || '')) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  getLineItemProperties() {
    this.updateStateProperties();
    const properties = {};

    this.getPropertyControls().forEach((input) => {
      if (input.disabled || !input.name || !input.name.startsWith('properties[')) return;
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      if (input.type === 'file') return;

      const propertyName = input.name.match(/^properties\[(.+)\]$/)?.[1];
      if (!this.shouldIncludeProperty(propertyName, input)) return;
      if (propertyName && input.value !== '') properties[this.normalizePropertyName(propertyName)] = input.value;
    });

    if (this.textSaved) {
      properties['Text Embroidery'] = 'Yes';
      properties['First Line'] = this.line1.value.trim();
      if (this.line2.value.trim()) properties['Second Line'] = this.line2.value.trim();
      const textPosition = this.textPositionInputs.find((input) => input.checked)?.value;
      if (textPosition) properties['Text Position'] = textPosition;
    }

    if (this.logoSaved) {
      properties['Logo Embroidery'] = 'Yes';
      properties['Logo Name'] = this.logoName.value.trim();
      const logoPosition = this.logoPositionInputs.find((input) => input.checked)?.value;
      if (logoPosition) properties['Logo Position'] = logoPosition;
      properties['Digitisation'] = this.currentDigitisationLabel();
    }

    return properties;
  }

  shouldIncludeProperty(propertyName, input) {
    if (!this.shouldIncludePropertyName(propertyName)) return false;
    if (['Logo Embroidery', 'Logo Name', 'Logo Position', 'Digitisation'].includes(propertyName)) return this.logoSaved;
    if (/logo|upload|file/i.test(propertyName + ' ' + (input.closest('[class*="upload"], [id*="upload"], [data-upload-lift], [data-cloudlift]')?.textContent || ''))) {
      return this.logoSaved && this.logoNeedsDigitisation();
    }
    return true;
  }

  shouldIncludePropertyName(propertyName) {
    if (!propertyName) return false;
    if (['Text Embroidery', 'First Line', 'Second Line', 'Text Position'].includes(propertyName)) return this.textSaved;
    if (['Logo Embroidery', 'Logo Name', 'Logo Position', 'Digitisation'].includes(propertyName)) return this.logoSaved;
    if (/logo|upload|file/i.test(propertyName)) return this.logoSaved && this.logoNeedsDigitisation();
    return true;
  }

  normalizePropertyName(propertyName) {
    return propertyName === 'upload' ? 'Upload' : propertyName;
  }

  getPropertyControls() {
    const form = this.closest('form');
    return [
      ...Array.from(this.querySelectorAll('[name^="properties["]')),
      ...Array.from(this.modal?.querySelectorAll('[name^="properties["]') || []),
      ...Array.from(form?.querySelectorAll('[name^="properties["]') || []).filter((input) => !this.contains(input) && !this.modal?.contains(input))
    ];
  }
  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError() {
    this.error.textContent = '';
    this.error.hidden = true;
  }

  bridgeUploadLiftField() {
    const adoptField = () => {
      const form = this.closest('form');
      const scope = this.closest('product-info') || this.closest('.product__info-container') || document;
      if (!form || !this.uploadTarget) return;

      const candidates = Array.from(scope.querySelectorAll('[class*="upload"], [id*="upload"], [data-upload-lift], [data-cloudlift]')).filter((element) => {
        if (this.contains(element)) return false;
        if (this.uploadTarget.contains(element)) return false;
        if (element.classList.contains('embroidery-upload-adopted')) return false;
        const text = element.textContent || '';
        const html = element.outerHTML || '';
        return /upload|logo|cloudlift/i.test(text + html);
      });

      const field = candidates.find((element) => element.querySelector('input, button, iframe, [role="button"]'));
      if (!field) return false;

      field.classList.add('embroidery-upload-source');
      this.uploadTarget.innerHTML = '';
      this.uploadTarget.appendChild(field);
      field.classList.add('embroidery-upload-adopted');
      field.classList.remove('embroidery-upload-source');
      return true;
    };

    if (adoptField()) return;

    const scope = this.closest('product-info') || this.closest('.product__info-container') || document;
    let attempts = 0;
    const maxAttempts = 30;
    const observer = new MutationObserver(() => {
      if (adoptField()) observer.disconnect();
    });

    observer.observe(scope, { childList: true, subtree: true });

    const retry = window.setInterval(() => {
      attempts += 1;
      if (adoptField() || attempts >= maxAttempts) {
        window.clearInterval(retry);
        observer.disconnect();
      }
    }, 200);
  }

  updatePreviewImage(variant) {
    const image = variant?.featured_image || variant?.featured_media?.preview_image;
    if (!this.previewImage || !image?.src) return;

    this.previewImage.src = image.src;
    this.previewImage.alt = image.alt || this.previewImage.alt;
  }
}

if (!customElements.get('product-embroidery')) {
  customElements.define('product-embroidery', ProductEmbroidery);
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const embroidery = this.form.querySelector('.product-embroidery');
      embroidery?.setAddToCartLoading?.(true);
      const embroideryDetails = embroidery?.prepareGroup ? embroidery.prepareGroup() : null;
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      
      console.log('formData',formData)
      if (embroideryDetails) {
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(this.buildEmbroideryCartPayload(formData, embroideryDetails, embroidery));
      } else {
        delete config.headers['Content-Type'];
        config.body = formData;
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then(async (response) => {
          document.dispatchEvent(new CustomEvent('globo.preorder.update.cart'))
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (embroideryDetails && window.syncAggregateEmbroideryCartItems) {
            await window.syncAggregateEmbroideryCartItems();
            response = await this.fetchCartStateForRender(response);
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          if (!this.error && embroidery?.resetAfterCartAdd) embroidery.resetAfterCartAdd();
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          embroidery?.setAddToCartLoading?.(false);
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    async fetchCartStateForRender(originalResponse) {
      if (!this.cart?.getSectionsToRender) return originalResponse;

      const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
      const [cartState, sections] = await Promise.all([
        fetch(`${routes.cart_url}.js`).then((response) => response.json()),
        fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`).then((response) => response.json())
      ]);

      return {
        ...originalResponse,
        ...cartState,
        sections: {
          ...originalResponse.sections,
          ...sections
        }
      };
    }

    buildEmbroideryCartPayload(formData, embroideryDetails, embroidery) {
      const quantity = parseInt(formData.get('quantity') || this.form.querySelector('.quantity__input')?.value || '1', 10);
      const properties = embroidery?.getLineItemProperties ? embroidery.getLineItemProperties() : {};
      const addonBreakdown = embroidery?.getAddonBreakdownProperties
        ? embroidery.getAddonBreakdownProperties(quantity)
        : {
          embroidery: {},
          digitization: {}
        };

      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch && typeof value === 'string' && value !== '') {
          if (!embroidery?.shouldIncludePropertyName || embroidery.shouldIncludePropertyName(propertyMatch[1])) {
            const propertyName = embroidery?.normalizePropertyName ? embroidery.normalizePropertyName(propertyMatch[1]) : propertyMatch[1];
            properties[propertyName] = value;
          }
        }
      });

      const items = [
        {
          id: parseInt(formData.get('id'), 10),
          quantity,
          properties
        }
      ];

      if (embroideryDetails.embroideryVariantId && embroideryDetails.embroideryUnits > 0) {
        items.push({
          id: embroideryDetails.embroideryVariantId,
          quantity: quantity * embroideryDetails.embroideryUnits,
          properties: addonBreakdown.embroidery
        });
      }

      if (embroideryDetails.digitizationRequired && embroideryDetails.digitizationVariantId) {
        items.push({
          id: embroideryDetails.digitizationVariantId,
          quantity: 1,
          properties: addonBreakdown.digitization
        });
      }

      return {
        items,
        sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
        sections_url: window.location.pathname
      };
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
